import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { Bell, Mail, Smartphone, Plus, X, Search, ToggleLeft, ToggleRight, Send } from "lucide-react";
import { SettingsHeader } from "./_shared";
import MyPushPrefsPanel from "./MyPushPrefsPanel";
import CriticalPermsAlertBlock from "./CriticalPermsAlertBlock";
import MissionTypeChangeAlertBlock from "./MissionTypeChangeAlertBlock";

const CATEGORY_ORDER = ["CRA", "Missions", "Clients", "Billing", "Freelance", "Account"];

export default function NotificationsSettings() {
  const { user } = useAuth();
  const [catalog, setCatalog] = useState([]);
  const [prefs, setPrefs] = useState({});
  const [users, setUsers] = useState([]);
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState("");
  const [openPicker, setOpenPicker] = useState(null); // notif key whose recipients picker is open
  // Iter-106: roles whose owners never receive notifs for their own client/mission/user actions
  const [muteRoles, setMuteRoles] = useState([]);
  const [testingPush, setTestingPush] = useState(false);

  const load = async () => {
    const [c, u] = await Promise.all([
      api.get("/notifications/settings/catalog"),
      api.get("/users").catch(() => ({ data: [] })),
    ]);
    setCatalog(c.data.catalog || []);
    setPrefs(c.data.preferences || {});
    setMuteRoles(c.data.mute_self_action_roles || []);
    setUsers(u.data || []);
  };
  useEffect(() => { load(); }, []);

  const userMap = useMemo(() => Object.fromEntries(users.map((u) => [u.user_id, u])), [users]);

  const grouped = useMemo(() => {
    const f = filter.trim().toLowerCase();
    const matches = (n) => !f || n.label.toLowerCase().includes(f) || n.key.toLowerCase().includes(f) || (n.description || "").toLowerCase().includes(f);
    const out = {};
    for (const n of catalog) {
      if (!matches(n)) continue;
      const cat = n.category || "Other";
      (out[cat] = out[cat] || []).push(n);
    }
    return out;
  }, [catalog, filter]);

  const patch = (key, partial) => {
    // Iter-104: every toggle commits immediately. We update local state first
    // (optimistic), then PUT the single delta so the user gets instant feedback
    // and the network round-trip is silent.
    setPrefs((prev) => {
      const next = { ...prev, [key]: { ...(prev[key] || {}), ...partial } };
      // Fire-and-forget PUT with the merged config for this single key
      const cfg = next[key];
      api.put("/notifications/settings", {
        preferences: {
          [key]: {
            enabled: cfg.enabled !== false,
            inapp_enabled: !!cfg.inapp_enabled,
            email_enabled: !!cfg.email_enabled,
            extra_email_user_ids: cfg.extra_email_user_ids || [],
          },
        },
      }).catch((e) => {
        toast.error(e?.response?.data?.detail || "Échec de sauvegarde");
      });
      return next;
    });
  };

  // Kept for legacy callers (e.g. category-bulk toggles); now a no-op since each
  // patch auto-saves. Left here so deletes from the UI keep working.
  const save = async () => {};

  const removeRecipient = (key, uid) => {
    const cur = prefs[key]?.extra_email_user_ids || [];
    patch(key, { extra_email_user_ids: cur.filter((x) => x !== uid) });
  };

  const addRecipient = (key, uid) => {
    const cur = prefs[key]?.extra_email_user_ids || [];
    if (cur.includes(uid)) return;
    patch(key, { extra_email_user_ids: [...cur, uid] });
  };

  const toggleCategory = (cat, on) => {
    const items = grouped[cat] || [];
    setPrefs((prev) => {
      const next = { ...prev };
      for (const n of items) {
        next[n.key] = { ...(prev[n.key] || {}), enabled: on };
      }
      return next;
    });
  };

  const isCategoryAllOn = (cat) =>
    (grouped[cat] || []).every((n) => (prefs[n.key]?.enabled ?? true) !== false);

  const applyPreset = async (preset) => {
    if (!window.confirm(
      preset === "silence"
        ? "Désactiver TOUTES les notifications (in-app + email) ?"
        : preset === "default"
        ? "Réinitialiser toutes les préférences aux valeurs par défaut du catalogue ?"
        : preset === "all_inapp"
        ? "Activer toutes les notifications in-app (les emails restent inchangés) ?"
        : "Activer TOUTES les notifications (in-app + email) ?"
    )) return;
    setSaving(true);
    try {
      const r = await api.post("/notifications/settings/preset", { preset });
      setPrefs(r.data.preferences || {});
      const labels = { silence: "Silence radio", default: "Mode normal", all_inapp: "Tout activer (in-app)", all_on: "Tout activer (in-app + email)" };
      toast.success(`Profil appliqué : ${labels[preset]}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors de l'application du profil");
    } finally { setSaving(false); }
  };

  const visibleCats = CATEGORY_ORDER.filter((c) => grouped[c]?.length).concat(
    Object.keys(grouped).filter((c) => !CATEGORY_ORDER.includes(c))
  );

  // Iter-106: toggle self-action mute for a given role
  const toggleMuteRole = async (role) => {
    const next = muteRoles.includes(role)
      ? muteRoles.filter((r) => r !== role)
      : [...muteRoles, role];
    setMuteRoles(next);
    try {
      await api.put("/notifications/settings/mute-self-actions", { roles: next });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec de sauvegarde");
      setMuteRoles(muteRoles); // rollback
    }
  };

  // Iter-172: send a test push to confirm end-to-end push delivery (CEO only).
  const sendTestPush = async () => {
    setTestingPush(true);
    try {
      const r = await api.post("/push/test");
      const { subscriptions, delivered } = r.data || {};
      if (delivered > 0) {
        toast.success(`Push sent to ${delivered}/${subscriptions} device(s) — check your phone or browser.`);
      } else {
        toast.warning(`No device received the push (${subscriptions} subscription(s) registered, 0 delivered). Dead subscriptions were pruned — re-enable notifications.`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send test push");
    } finally {
      setTestingPush(false);
    }
  };

  return (
    <div className="p-4 md:p-8 lg:p-12 max-w-[1100px]" data-testid="notifications-settings-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-2">
        <SettingsHeader
          kicker="Notifications"
          title="Notification settings"
          description="Toggle each notification type on or off, choose how it is delivered (in-app and/or email), and add extra email recipients on top of the natural recipient. Les modifications sont enregistrées automatiquement."
        />
        {user?.role === "CEO" && (
          <button
            type="button"
            onClick={sendTestPush}
            disabled={testingPush}
            data-testid="notif-test-push-btn"
            title="Send a Web Push test notification to your registered devices to confirm the integration works."
            className="inline-flex items-center justify-center gap-2 px-4 h-10 bg-brand-navy text-white text-sm font-medium rounded-md hover:bg-brand-navy-600 disabled:opacity-50 shrink-0"
          >
            <Send size={14} /> {testingPush ? "Sending…" : "Test push"}
          </button>
        )}
      </div>

      {/* Iter-173: per-user push preferences (categories + quiet hours) */}
      <MyPushPrefsPanel />

      {/* Iter-106: Mute self actions — top-of-page role selector */}
      <div className="mb-6 p-4 bg-amber-50/60 border border-amber-200 rounded-md" data-testid="mute-self-actions">        <div className="flex items-center gap-2 mb-2">
          <span className="text-[11px] uppercase tracking-[0.2em] text-amber-700 font-mono">Mute self actions</span>
        </div>
        <p className="text-xs text-amber-900/80 leading-relaxed mb-3 max-w-2xl">
          For each role below, users will <strong>not</strong> receive in-app badges or emails when they personally
          create / update a <strong>client</strong>, a <strong>mission</strong>, or a <strong>user</strong>. Others are still notified normally.
        </p>
        <div className="flex flex-wrap gap-2">
          {["CEO", "ADMIN", "MANAGER", "GESTIONNAIRE"].map((role) => {
            const active = muteRoles.includes(role);
            return (
              <button
                key={role}
                type="button"
                onClick={() => toggleMuteRole(role)}
                data-testid={`mute-self-role-${role}`}
                className={`inline-flex items-center gap-1.5 px-3 h-8 rounded-md text-xs font-medium border transition-colors ${
                  active
                    ? "bg-amber-600 text-white border-amber-700 hover:bg-amber-700"
                    : "bg-white text-zinc-700 border-zinc-300 hover:bg-amber-100 hover:text-amber-900 hover:border-amber-300"
                }`}
              >
                <span className={`inline-block w-3.5 h-3.5 rounded-sm border ${active ? "bg-white border-white" : "bg-transparent border-zinc-400"}`}>
                  {active && <span className="block text-amber-700 leading-none text-[10px] text-center">✓</span>}
                </span>
                {role}
              </button>
            );
          })}
        </div>
      </div>

      {/* Iter-193w — NDF lifecycle email Cc section (CEO-only) */}
      {user?.role === "CEO" && <NdfEmailCcBlock users={users} />}

      {/* Iter-202c — Critical permissions alert (CEO-only) */}
      {user?.role === "CEO" && <CriticalPermsAlertBlock />}

      {/* Iter-204c — Mission type change alert (CEO-only) */}
      {user?.role === "CEO" && <MissionTypeChangeAlertBlock />}

      {/* Iter-97: one-click preset profiles */}
      <div className="flex flex-wrap items-center gap-2 mb-6 mt-4 p-3 bg-brand-cream/40 border border-brand-cream rounded-md" data-testid="notif-presets">
        <span className="text-[11px] uppercase tracking-[0.2em] text-zinc-500 font-mono mr-2">Profils</span>
        <button
          type="button"
          onClick={() => applyPreset("silence")}
          disabled={saving}
          data-testid="notif-preset-silence"
          className="inline-flex items-center gap-1.5 px-3 h-8 rounded-md text-xs border border-zinc-200 bg-white text-zinc-700 hover:bg-red-50 hover:border-red-200 hover:text-red-700 transition-colors disabled:opacity-50"
          title="Désactive toutes les notifications (in-app + email)"
        >
          <ToggleLeft size={13} /> Silence radio
        </button>
        <button
          type="button"
          onClick={() => applyPreset("default")}
          disabled={saving}
          data-testid="notif-preset-default"
          className="inline-flex items-center gap-1.5 px-3 h-8 rounded-md text-xs border border-zinc-200 bg-white text-zinc-700 hover:bg-brand-navy/5 hover:border-brand-navy/30 hover:text-brand-navy transition-colors disabled:opacity-50"
          title="Réinitialise aux valeurs par défaut du catalogue"
        >
          <Bell size={13} /> Mode normal
        </button>
        <button
          type="button"
          onClick={() => applyPreset("all_on")}
          disabled={saving}
          data-testid="notif-preset-all-on"
          className="inline-flex items-center gap-1.5 px-3 h-8 rounded-md text-xs border border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 transition-colors disabled:opacity-50"
          title="Active TOUT (in-app + email) — utile en période critique (rentrée, fin d'exercice…)"
        >
          <ToggleRight size={13} /> Tout activer
        </button>
        <span className="text-[10px] text-zinc-500 ml-2 max-w-[260px] hidden md:inline">
          Un clic pour passer de "tout sauf urgences" à "tout in-app" — vos destinataires email custom sont préservés.
        </span>
      </div>

      <div className="relative mb-6 max-w-md">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
        <input
          data-testid="notif-search"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Search by name, key or description…"
          className="w-full pl-9 pr-3 h-10 border border-zinc-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-brand-navy/30"
        />
      </div>

      {visibleCats.length === 0 && (
        <div className="text-sm text-zinc-500" data-testid="notif-empty">No notification matches your search.</div>
      )}

      {visibleCats.map((cat) => {
        const allOn = isCategoryAllOn(cat);
        return (
        <div key={cat} className="mb-8">
          <div className="flex items-center justify-between mb-3">
            <div className="text-[11px] uppercase tracking-[0.25em] text-zinc-500 font-mono" data-testid={`notif-cat-${cat.toLowerCase()}`}>{cat}</div>
            <button
              onClick={() => toggleCategory(cat, !allOn)}
              data-testid={`notif-cat-${cat.toLowerCase()}-bulk-toggle`}
              className={`inline-flex items-center gap-1.5 px-2.5 h-7 rounded-md text-[11px] font-medium border transition-colors ${
                allOn
                  ? "border-zinc-200 text-zinc-700 hover:bg-zinc-50"
                  : "border-emerald-300 bg-emerald-50 text-emerald-800 hover:bg-emerald-100"
              }`}
              title={allOn ? `Disable every ${cat} notification` : `Enable every ${cat} notification`}
            >
              {allOn ? <ToggleLeft size={13} /> : <ToggleRight size={13} />}
              {allOn ? "Disable all" : "Enable all"}
            </button>
          </div>
          <div className="bg-white border border-zinc-200 rounded-lg overflow-hidden divide-y divide-zinc-100">
            {grouped[cat].map((n) => {
              const cfg = prefs[n.key] || { enabled: true, inapp_enabled: true, email_enabled: false, extra_email_user_ids: [] };
              const recipients = cfg.extra_email_user_ids || [];
              // Iter-104: row tinted in pale-orange when the type is active (inapp or email),
              // white when fully disabled. Subtle visual scan for "what's currently on".
              const rowActive = cfg.enabled !== false && (cfg.inapp_enabled !== false || cfg.email_enabled === true);
              return (
                <div
                  key={n.key}
                  data-testid={`notif-row-${n.key}`}
                  className={`p-4 md:p-5 transition-colors ${rowActive ? "bg-orange-50/60" : "bg-white"}`}
                >
                  <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Bell size={14} className="text-zinc-500" />
                        <span className="font-medium text-sm">{n.label}</span>
                        <code className="text-[10px] font-mono text-zinc-400 bg-zinc-50 px-1.5 py-0.5 rounded">{n.key}</code>
                      </div>
                      {n.description && <div className="text-xs text-zinc-500 mt-1 leading-relaxed">{n.description}</div>}
                    </div>

                    <div className="flex items-center gap-5 md:gap-6 shrink-0">
                      <label className="flex items-center gap-2 text-xs">
                        <Switch data-testid={`notif-${n.key}-enabled`} checked={!!cfg.enabled} onCheckedChange={(v) => patch(n.key, { enabled: v })} />
                        <span className="font-mono uppercase tracking-wider text-zinc-600">Active</span>
                      </label>
                    </div>
                  </div>

                  {/* Channels — only shown when active */}
                  {cfg.enabled !== false && (
                    <div className="mt-3 pt-3 border-t border-zinc-100 flex flex-col gap-3">
                      <div className="flex flex-wrap items-center gap-5">
                        <label className="inline-flex items-center gap-2 text-xs">
                          <Switch data-testid={`notif-${n.key}-inapp`} checked={!!cfg.inapp_enabled} onCheckedChange={(v) => patch(n.key, { inapp_enabled: v })} />
                          <Smartphone size={13} className="text-zinc-500" />
                          <span className="text-zinc-700">In-app</span>
                        </label>
                        <label className="inline-flex items-center gap-2 text-xs">
                          <Switch data-testid={`notif-${n.key}-email`} checked={!!cfg.email_enabled} onCheckedChange={(v) => patch(n.key, { email_enabled: v })} />
                          <Mail size={13} className="text-zinc-500" />
                          <span className="text-zinc-700">Email</span>
                        </label>
                      </div>

                      {/* Recipients — only when email is on */}
                      {cfg.email_enabled && (
                        <div data-testid={`notif-${n.key}-recipients`} className="bg-zinc-50/70 border border-zinc-100 rounded-md p-3">
                          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2 font-mono">Extra email recipients</div>
                          <div className="flex flex-wrap items-center gap-2">
                            {recipients.length === 0 && (
                              <span className="text-xs text-zinc-400">No extra recipient — only the natural recipient receives the email.</span>
                            )}
                            {recipients.map((uid) => {
                              const u = userMap[uid];
                              return (
                                <span key={uid} className="inline-flex items-center gap-1.5 pl-2 pr-1 py-0.5 bg-white border border-zinc-200 rounded text-xs">
                                  <span className="truncate max-w-[180px]">{u?.name || uid}</span>
                                  <button
                                    onClick={() => removeRecipient(n.key, uid)}
                                    data-testid={`notif-${n.key}-rm-${uid}`}
                                    className="w-4 h-4 inline-flex items-center justify-center rounded hover:bg-zinc-100 text-zinc-500"
                                    aria-label="remove"
                                  >
                                    <X size={10} />
                                  </button>
                                </span>
                              );
                            })}
                            <button
                              onClick={() => setOpenPicker(openPicker === n.key ? null : n.key)}
                              data-testid={`notif-${n.key}-add`}
                              className="inline-flex items-center gap-1 px-2 h-7 border border-dashed border-zinc-300 rounded text-xs text-zinc-700 hover:bg-white"
                            >
                              <Plus size={12} /> Add
                            </button>
                          </div>
                          {openPicker === n.key && (
                            <div className="mt-2 max-h-48 overflow-y-auto bg-white border border-zinc-200 rounded-md text-xs">
                              {users.filter((u) => !recipients.includes(u.user_id)).map((u) => (
                                <button
                                  key={u.user_id}
                                  onClick={() => { addRecipient(n.key, u.user_id); setOpenPicker(null); }}
                                  data-testid={`notif-${n.key}-pick-${u.user_id}`}
                                  className="w-full text-left px-3 py-1.5 hover:bg-zinc-50 flex items-center justify-between gap-2"
                                >
                                  <span className="truncate">{u.name}</span>
                                  <span className="text-[10px] text-zinc-400 truncate font-mono">{u.email}</span>
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
        );
      })}
    </div>
  );
}


// Iter-193w — CEO-only block: NDF lifecycle email Cc settings.
function NdfEmailCcBlock({ users }) {
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [includeCeo, setIncludeCeo] = useState(true);
  const [includeGest, setIncludeGest] = useState(true);
  const [ccUserIds, setCcUserIds] = useState([]);
  const [extraEmails, setExtraEmails] = useState([]);
  const [showPicker, setShowPicker] = useState(false);
  const [emailInput, setEmailInput] = useState("");

  useEffect(() => {
    api.get("/settings").then((r) => {
      const s = r.data || {};
      setIncludeCeo(s.ndf_email_include_ceo !== false);
      setIncludeGest(s.ndf_email_include_gestionnaires !== false);
      setCcUserIds(Array.isArray(s.ndf_email_cc_user_ids) ? s.ndf_email_cc_user_ids : []);
      setExtraEmails(Array.isArray(s.ndf_email_cc_extra_emails) ? s.ndf_email_cc_extra_emails : []);
      setLoaded(true);
    }).catch(() => setLoaded(true));
  }, []);

  const save = async (patch) => {
    setSaving(true);
    try {
      await api.put("/settings", patch);
      toast.success("Cc NDF mis à jour");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec");
    } finally { setSaving(false); }
  };

  const addEmail = () => {
    const e = (emailInput || "").trim();
    if (!e) return;
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(e)) { toast.error(`Email invalide : ${e}`); return; }
    if (extraEmails.map((x) => x.toLowerCase()).includes(e.toLowerCase())) { toast.error("Déjà présent"); return; }
    const next = [...extraEmails, e];
    setExtraEmails(next);
    setEmailInput("");
    save({ ndf_email_cc_extra_emails: next });
  };

  const removeEmail = (e) => {
    const next = extraEmails.filter((x) => x !== e);
    setExtraEmails(next);
    save({ ndf_email_cc_extra_emails: next });
  };

  const toggleUser = (uid) => {
    const next = ccUserIds.includes(uid) ? ccUserIds.filter((x) => x !== uid) : [...ccUserIds, uid];
    setCcUserIds(next);
    save({ ndf_email_cc_user_ids: next });
  };

  const userMap = useMemo(() => Object.fromEntries(users.map((u) => [u.user_id, u])), [users]);
  if (!loaded) return null;

  return (
    <div className="mb-8 bg-white border border-zinc-200 rounded-lg p-4" data-testid="ndf-cc-settings">
      <div className="flex items-center gap-2 mb-1">
        <Mail size={14} className="text-zinc-700" />
        <h2 className="font-medium text-sm">Cc des emails NDF (CEO uniquement)</h2>
      </div>
      <p className="text-xs text-zinc-500 mb-3">
        Lors de l'envoi automatique des emails de cycle de vie d'une note de frais
        (soumission, validation, rejet, annulation, paiement), les adresses ci-dessous
        seront ajoutées en copie carbone.
      </p>
      <div className="flex flex-wrap gap-4 mb-3">
        <label className="inline-flex items-center gap-2 text-sm">
          <Switch
            data-testid="ndf-cc-include-ceo"
            checked={includeCeo}
            onCheckedChange={(v) => { setIncludeCeo(v); save({ ndf_email_include_ceo: !!v }); }}
          />
          <span className="text-zinc-700">Inclure le CEO</span>
        </label>
        <label className="inline-flex items-center gap-2 text-sm">
          <Switch
            data-testid="ndf-cc-include-gest"
            checked={includeGest}
            onCheckedChange={(v) => { setIncludeGest(v); save({ ndf_email_include_gestionnaires: !!v }); }}
          />
          <span className="text-zinc-700">Inclure tous les Gestionnaires</span>
        </label>
      </div>
      <div className="border-t border-zinc-100 pt-3">
        <div className="text-[11px] uppercase tracking-[0.18em] text-zinc-500 font-mono mb-2">Utilisateurs additionnels en Cc</div>
        <div className="flex flex-wrap gap-1 mb-2">
          {ccUserIds.length === 0 && <span className="text-xs text-zinc-400 italic">Aucun</span>}
          {ccUserIds.map((uid) => {
            const u = userMap[uid];
            return (
              <span key={uid} className="inline-flex items-center gap-1 px-2 h-7 rounded-full bg-zinc-100 text-xs" data-testid={`ndf-cc-user-${uid}`}>
                {u?.name || uid}
                <button onClick={() => toggleUser(uid)} className="text-zinc-500 hover:text-red-600" disabled={saving}>
                  <X size={11} />
                </button>
              </span>
            );
          })}
        </div>
        <button onClick={() => setShowPicker((s) => !s)} className="text-xs text-blue-700 hover:underline inline-flex items-center gap-1" data-testid="ndf-cc-user-picker-toggle">
          <Plus size={11} /> Ajouter un utilisateur
        </button>
        {showPicker && (
          <div className="mt-2 border border-zinc-200 rounded p-2 bg-zinc-50/40 max-h-40 overflow-auto" data-testid="ndf-cc-user-picker">
            {users.filter((u) => !ccUserIds.includes(u.user_id) && u.is_active !== false).map((u) => (
              <button key={u.user_id} onClick={() => { toggleUser(u.user_id); setShowPicker(false); }}
                className="block w-full text-left px-2 py-1 text-xs hover:bg-zinc-100 rounded">
                {u.name} <span className="text-zinc-400">({u.role})</span>
              </button>
            ))}
          </div>
        )}
      </div>
      <div className="border-t border-zinc-100 pt-3 mt-3">
        <div className="text-[11px] uppercase tracking-[0.18em] text-zinc-500 font-mono mb-2">Emails libres (autres domaines)</div>
        <div className="flex flex-wrap gap-1 mb-2">
          {extraEmails.length === 0 && <span className="text-xs text-zinc-400 italic">Aucun</span>}
          {extraEmails.map((e) => (
            <span key={e} className="inline-flex items-center gap-1 px-2 h-7 rounded-full bg-blue-50 text-blue-900 text-xs font-mono" data-testid={`ndf-cc-email-${e}`}>
              {e}
              <button onClick={() => removeEmail(e)} className="text-blue-700 hover:text-red-600" disabled={saving}>
                <X size={11} />
              </button>
            </span>
          ))}
        </div>
        <div className="flex gap-2">
          <input
            value={emailInput}
            onChange={(e) => setEmailInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addEmail(); } }}
            placeholder="comptable@cabinet.com"
            data-testid="ndf-cc-email-input"
            className="flex-1 h-8 px-2 border border-zinc-200 rounded text-sm"
          />
          <button onClick={addEmail} data-testid="ndf-cc-email-add" className="px-3 h-8 bg-zinc-950 text-white text-xs rounded hover:bg-zinc-800 inline-flex items-center gap-1">
            <Plus size={11} /> Ajouter
          </button>
        </div>
      </div>
    </div>
  );
}
